import { createAsyncThunk } from '@reduxjs/toolkit'

import {
  MINIMUM_SECTION_SWITCH_DURATION_SECS,
  MINIMUM_VIZ_SWITCH_PART_DURATION,
  SECTION_SWITCH_FLASH_SECS,
  VIZ_SWITCH_IN_DELAY_SECS,
  VIZ_SWITCH_OUT_EARLY_SECS,
} from '../constants/constants'
import { RootState } from '../reducers'
import { selectVisualizationInfo } from '../selectors/session-selectors'
import { selectViewSettings } from '../selectors/settings-selectors'
import Gamer from '../services/Gamer'
import player from '../services/TrackMixer'
import { Section } from '../types'

type SectionInfo2 = {
  section: Section
  canViz: boolean
  canFlash: boolean
}

const getSectionInfo = (sections: Section[]): SectionInfo2[] => {
  let lastStart = -1
  const { trackDuration } = player // TODO: better way to get duration from?
  const sectionInfos = sections.reduce((acc: SectionInfo2[], section: Section, index: number) => {
    const isEmpty = !section.lines.length
    const prevSection = index > 0 ? sections[index - 1] : null
    const prevSectionStartTime = prevSection?.referenceTime || 0
    const sectionStartTime = section.referenceTime || 0
    const prevSectionDuration = prevSection ? sectionStartTime - prevSectionStartTime : 0
    let canFlash = !isEmpty && prevSectionDuration > MINIMUM_SECTION_SWITCH_DURATION_SECS
    let canViz = isEmpty
    if (isEmpty) {
      let startTime = section.referenceTime
      if (startTime === null) {
        console.log('ignoring null section start', section)
        canViz = false
      } else {
        if (startTime === 0 && prevSection) {
          if (prevSection.lines.length) {
            const lastline = prevSection.lines[prevSection.lines.length - 1]
            if (lastline.words.length) {
              const lastWord = lastline.words[lastline.words.length - 1]
              if (lastWord.referenceTime) {
                startTime = lastWord.referenceTime
                section.referenceTime = startTime
              }
            }
          }
        }
        if (startTime <= lastStart) {
          console.log('ignoring out-of-order empty section start', section)
          canViz = false
        } else {
          const nextSection = index + 1 < sections.length ? sections[index + 1] : null
          const referenceEndTime = nextSection ? nextSection.referenceTime : trackDuration
          const endTime = referenceEndTime !== null ? referenceEndTime : trackDuration
          const partLength = endTime - startTime
          if (partLength < MINIMUM_VIZ_SWITCH_PART_DURATION) {
            // console.log('ignoring too-short empty section', section)
            canViz = false
          }
          lastStart = startTime
        }
      }
    }
    return [
      ...acc,
      {
        section,
        canFlash,
        canViz,
      },
    ]
  }, [])

  return sectionInfos
}
const scheduleSectionEvents = createAsyncThunk<void, Gamer, { state: RootState }>(
  'playAction/scheduleSectionAnimations',
  (gamer, { getState }) => {
    const { gamerIndex, vizBuilder } = gamer
    const state = getState()
    const { isShowSectionAnimation, isVizSwitchingEnabled } = selectViewSettings(gamerIndex)(state)
    const vizInfo = selectVisualizationInfo(state)
    const { defaultVizSlug, alternateVizSlug } = vizInfo[gamerIndex]
    const turnOn = (section: Section) => {
      // console.log('turning on animation for section:', section.index, section.label)
      section.elem?.classList.add('sectionAnimation')
    }
    const turnOff = (section: Section) => {
      // console.log('turning off animation for section:', section.index, section.label)
      section.elem?.classList.remove('sectionAnimation')
    }
    const switchIn = () => {
      vizBuilder.switchViz({ isSwitchingIn: true, vizSlug: alternateVizSlug })
    }
    const switchOut = () => {
      vizBuilder.switchViz({ isSwitchingIn: false, vizSlug: defaultVizSlug })
    }
    const sectionsInfo = getSectionInfo(vizBuilder.sections)
    let vizStarted = false
    sectionsInfo.forEach(({ section, canViz, canFlash }) => {
      gamer.removePartEvent(section)
      if (!isShowSectionAnimation && !isVizSwitchingEnabled) {
        return // nothing to do
      }
      // console.log('Scheduling animation for section:', section.index, section.label)
      if (canViz && vizStarted) {
        throw new Error('bad sectionInfo: viz never turned off')
      }
      if (canFlash && canViz) {
        throw new Error('bad sectionInfo: no flash before starting viz!')
      }
      const isSwitchIn = canViz && isVizSwitchingEnabled
      const isSwitchOut = vizStarted && isVizSwitchingEnabled
      const isFlash = canFlash && isShowSectionAnimation
      const deltaTime = vizStarted
        ? VIZ_SWITCH_OUT_EARLY_SECS
        : canViz
        ? -VIZ_SWITCH_IN_DELAY_SECS
        : VIZ_SWITCH_OUT_EARLY_SECS - 1
      vizStarted = isSwitchIn
      const eventCallback = (_time: number) => {
        if (isSwitchIn) {
          switchIn()
        } else {
          if (isSwitchOut) {
            switchOut()
          }
          if (isFlash) {
            turnOn(section)
            setTimeout(() => {
              turnOff(section)
            }, 1000 * SECTION_SWITCH_FLASH_SECS)
          }
        }
      }
      gamer.setPartEvent({
        eventCallback,
        part: section,
        useReferenceTime: true,
        deltaTime,
      })
    })
    vizBuilder.isVizSwitchingScheduled = true
  }
)

export default scheduleSectionEvents
